import { Box, Typography } from '@mui/material';
import { FC } from 'react';
import { DocCallout, DocCodeBlock, DocSection } from '../docs-components';

const ErrorHandling: FC = () => (
  <Box>
    <Typography variant="h4" fontWeight={700} gutterBottom>
      Error Handling
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      The Thermia API uses standard HTTP status codes along with structured JSON error bodies to indicate the outcome of every request.
    </Typography>

    <DocSection title="Error Response Format">
      <Typography variant="body1" sx={{ mb: 1.5 }}>
        All error responses share the same JSON structure:
      </Typography>
      <DocCodeBlock language="json">
        {`{
  "error": {
    "code": "DEVICE_NOT_FOUND",
    "message": "No device with the given ID was found for this API key.",
    "requestId": "req_01hx3k9z7y"
  }
}`}
      </DocCodeBlock>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 2 }}>
        {[
          ['error.code', 'Machine-readable error code — use this in your code, not the message.'],
          ['error.message', 'Human-readable description of the problem.'],
          ['error.requestId', 'Unique ID for this request. Include it when contacting support.']
        ].map(([field, desc]) => (
          <Typography key={field} variant="body2">
            <code>{field}</code> — {desc}
          </Typography>
        ))}
      </Box>
    </DocSection>

    <DocSection title="HTTP Status Codes">
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          '& th, & td': {
            textAlign: 'left',
            padding: '8px 12px',
            borderBottom: '1px solid',
            borderColor: 'divider',
            typography: 'body2'
          },
          '& th': { fontWeight: 700, bgcolor: 'action.hover' }
        }}>
        <thead>
          <tr>
            <th>Status</th>
            <th>Meaning</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['200 OK', 'The request succeeded.'],
            ['201 Created', 'A resource was successfully created (e.g., new webhook).'],
            ['400 Bad Request', 'The request was malformed or missing required fields.'],
            ['401 Unauthorized', 'The API key is missing, invalid, or expired.'],
            ['403 Forbidden', 'The API key does not have permission for this operation.'],
            ['404 Not Found', 'The requested resource (device, webhook, etc.) does not exist.'],
            ['409 Conflict', 'The operation conflicts with current state (e.g., duplicate webhook URL).'],
            ['422 Unprocessable Entity', 'The request was well-formed but failed validation rules.'],
            ['429 Too Many Requests', 'Rate limit exceeded. See Rate Limits for details.'],
            ['500 Internal Server Error', 'An unexpected error occurred on our side. Retry after a short delay.'],
            ['503 Service Unavailable', 'The API is temporarily unavailable. Retry with exponential back-off.']
          ].map(([status, meaning]) => (
            <tr key={status}>
              <td>
                <code>{status}</code>
              </td>
              <td>{meaning}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </DocSection>

    <DocSection title="Common Error Codes">
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          '& th, & td': {
            textAlign: 'left',
            padding: '8px 12px',
            borderBottom: '1px solid',
            borderColor: 'divider',
            typography: 'body2'
          },
          '& th': { fontWeight: 700, bgcolor: 'action.hover' }
        }}>
        <thead>
          <tr>
            <th>Code</th>
            <th>HTTP Status</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['INVALID_API_KEY', '401', 'The provided API key is not recognised.'],
            ['EXPIRED_API_KEY', '401', 'The API key has passed its expiry date.'],
            ['REVOKED_API_KEY', '401', 'The API key has been manually revoked.'],
            ['INSUFFICIENT_SCOPE', '403', 'The key does not have the required permission scope.'],
            ['DEVICE_NOT_FOUND', '404', 'No device matching the given ID for this key.'],
            ['DEVICE_OFFLINE', '503', 'The device is currently offline and cannot be reached.'],
            ['VALIDATION_ERROR', '422', 'One or more request fields failed validation.'],
            ['RATE_LIMIT_EXCEEDED', '429', 'Too many requests in the current time window.']
          ].map(([code, http, desc]) => (
            <tr key={code}>
              <td>
                <code>{code}</code>
              </td>
              <td>{http}</td>
              <td>{desc}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </DocSection>

    <DocSection title="Recommended Error Handling Pattern">
      <DocCodeBlock language="javascript">
        {`async function callApi(path, options = {}) {
  const response = await fetch(
    \`https://api.thermiaonline.com/v1\${path}\`,
    {
      ...options,
      headers: {
        Authorization: \`Bearer \${process.env.THERMIA_API_KEY}\`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    }
  );

  if (response.ok) return response.json();

  const { error } = await response.json();

  switch (error.code) {
    case 'EXPIRED_API_KEY':
    case 'REVOKED_API_KEY':
      // Prompt user to create a new key
      throw new Error('Your API key is no longer valid.');
    case 'RATE_LIMIT_EXCEEDED':
      // Implement back-off and retry
      throw new RateLimitError(response.headers.get('Retry-After'));
    case 'DEVICE_OFFLINE':
      // Surface a user-friendly message
      throw new Error('Device is currently offline.');
    default:
      throw new Error(\`API error [\${error.code}]: \${error.message}\`);
  }
}`}
      </DocCodeBlock>
    </DocSection>

    <DocCallout type="info">
      Include the <code>requestId</code> from the error response when contacting support — it allows us to quickly look up details about the failed
      request in our logs.
    </DocCallout>
  </Box>
);

export default ErrorHandling;
